/**
 * LISTE DURUMU ADRES CUBUGUNDA.
 *
 * Filtre, arama, siralama, sekme ve sayfa TAMAMEN sorgu dizesinde yasar.
 * Bilesen state'inde tutulsalardi filtrelenmis bir liste PAYLASILAMAZ ve
 * sayfa yenilendiginde KAYBOLURDU — web'de ikisi de kullanicinin bekledigi
 * seyler. Bonus: geri tusu filtreyi geri alir, bedavaya.
 *
 * SUNUCU SOZLESMESI adlari ile ARAYUZ adlari ayri tutuldu. Sunucu
 * `taskCreatedAtFrom` diyor, adres cubugu `from` diyor: uzun sunucu adlarini
 * URL'ye dokmek adres cubugunu okunmaz yapardi, ve bir gun sunucu adi
 * degistiginde kullanicilarin kaydettigi baglantilar kirilirdi.
 * Cevirim tek yerde: `filtersToServerParams`.
 */

export type ListFilters = {
  /** Serbest arama. */
  q: string;
  /** Para birimi COKLU secim; sunucuya virgullu tek dize olarak gider. */
  currencies: readonly string[];
  /** ISO tarih (yyyy-mm-dd). */
  createdFrom: string;
  createdTo: string;
  amountMin: string;
  amountMax: string;
  /** 'taskCreatedAt:desc' gibi — sunucunun sortMap anahtarlariyla BIREBIR. */
  sort: string;
};

export const EMPTY_FILTERS: ListFilters = Object.freeze({
  q: '',
  currencies: Object.freeze([]),
  createdFrom: '',
  createdTo: '',
  amountMin: '',
  amountMax: '',
  sort: '',
});

export type ListState = {
  /** Sekme anahtari — ekranin kendi listesinden. */
  tab: string;
  /** Sayfa ofseti (kayit sayisi). */
  offset: number;
  filters: ListFilters;
};

/** Adres cubugu parametre adlari — kisa ve okunur. */
const PARAM = {
  tab: 'sekme',
  offset: 'ofset',
  q: 'ara',
  currencies: 'kur',
  createdFrom: 'baslangic',
  createdTo: 'bitis',
  amountMin: 'min',
  amountMax: 'max',
  sort: 'sirala',
} as const;

const readString = (params: URLSearchParams, key: string): string => params.get(key) ?? '';

/** Sorgu dizesini liste durumuna cevirir. Bozuk deger = varsayilan. */
export const readListState = (params: URLSearchParams, defaultTab: string): ListState => {
  const rawOffset = Number.parseInt(readString(params, PARAM.offset), 10);
  const currencies = readString(params, PARAM.currencies)
    .split(',')
    .map((code) => code.trim())
    .filter((code) => code.length > 0);

  return {
    tab: readString(params, PARAM.tab) || defaultTab,
    // Negatif ya da sayi olmayan ofset sunucuya gonderilmez; bas sayfa.
    offset: Number.isFinite(rawOffset) && rawOffset > 0 ? rawOffset : 0,
    filters: {
      q: readString(params, PARAM.q),
      currencies,
      createdFrom: readString(params, PARAM.createdFrom),
      createdTo: readString(params, PARAM.createdTo),
      amountMin: readString(params, PARAM.amountMin),
      amountMax: readString(params, PARAM.amountMax),
      sort: readString(params, PARAM.sort),
    },
  };
};

/**
 * Liste durumunu sorgu dizesine yazar. BOS DEGERLER YAZILMAZ — aksi halde
 * dokunulmamis bir liste bile `?ara=&kur=&min=` gibi bir adres uretir ve iki
 * ayni durum iki farkli URL'ye karsilik gelir.
 */
export const writeListState = (state: ListState, defaultTab: string): URLSearchParams => {
  const params = new URLSearchParams();
  if (state.tab && state.tab !== defaultTab) params.set(PARAM.tab, state.tab);
  if (state.offset > 0) params.set(PARAM.offset, String(state.offset));

  const { filters } = state;
  if (filters.q) params.set(PARAM.q, filters.q);
  if (filters.currencies.length > 0) params.set(PARAM.currencies, filters.currencies.join(','));
  if (filters.createdFrom) params.set(PARAM.createdFrom, filters.createdFrom);
  if (filters.createdTo) params.set(PARAM.createdTo, filters.createdTo);
  if (filters.amountMin) params.set(PARAM.amountMin, filters.amountMin);
  if (filters.amountMax) params.set(PARAM.amountMax, filters.amountMax);
  if (filters.sort) params.set(PARAM.sort, filters.sort);
  return params;
};

/**
 * Filtreleri SUNUCU parametrelerine cevirir.
 *
 * Adlar dovizhub-mobile/src/redux/features/listPrefs `prefsToParams` ile
 * BIREBIR ayni — o esleme calisan bir istemcide dogrulandi; kisa adlarla
 * (createdFrom…) denemek, sessizce filtresiz donen bir liste uretirdi.
 */
export const filtersToServerParams = (filters: ListFilters): Record<string, string> => {
  const params: Record<string, string> = {};
  if (filters.currencies.length > 0) params.taskType = filters.currencies.join(',');
  if (filters.createdFrom) params.taskCreatedAtFrom = filters.createdFrom;
  if (filters.createdTo) params.taskCreatedAtTo = filters.createdTo;
  if (filters.amountMin) params.taskAmountMin = filters.amountMin;
  if (filters.amountMax) params.taskAmountMax = filters.amountMax;
  if (filters.q) params.q = filters.q;
  if (filters.sort) params.sort = filters.sort;
  return params;
};

/**
 * Kullanicinin gorebilecegi aktif filtre sayisi. SIRALAMA SAYILMAZ: siralama
 * her zaman bir degere sahip ve "filtreleri temizle" onu kaldirmamali.
 */
export const activeFilterCount = (filters: ListFilters): number => {
  let count = filters.currencies.length > 0 ? 1 : 0;
  if (filters.q) count += 1;
  if (filters.createdFrom) count += 1;
  if (filters.createdTo) count += 1;
  if (filters.amountMin) count += 1;
  if (filters.amountMax) count += 1;
  return count;
};

/**
 * Parametrelerin KARARLI imzasi — sorgu anahtarinin bir parcasi ve "filtre
 * degisti mi" sorusunun tek cevabi.
 *
 * Anahtarlar siralanir ve bos/undefined degerler atilir: {a:1} ile
 * {a:1, b:undefined} AYNI sorgudur; farkli imza uretselerdi liste kendi kendine
 * sifirlanip dururdu.
 */
export const paramsSignature = (params: Record<string, unknown>): string => {
  const clean: Record<string, unknown> = {};
  for (const key of Object.keys(params).sort()) {
    const value = params[key];
    if (value === undefined || value === null || value === '') continue;
    clean[key] = value;
  }
  return JSON.stringify(clean);
};

/**
 * Bir liste sayfasının belirli bir SEKMESİNE giden adres. Onaydan sonraki "Taleplerime dön"
 * bağlantısı varsayılan sekmeye (Açık Talepler) gidiyordu; onaylanan talep orada yok, kullanıcı
 * bir tık daha atıp Onaylanan'ı bulmak zorundaydı (M5, 2026-10-02).
 */
export const listTabHref = (path: string, tab: string, defaultTab: string): string => {
  const query = writeListState({ tab, offset: 0, filters: EMPTY_FILTERS }, defaultTab).toString();
  return query ? `${path}?${query}` : path;
};
